import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getTestCaseHistory, toggleStar, TestCaseHistoryItem } from '../api'

interface TestCaseHistoryProps {
  onDeleteRequest: (jobId: string) => void
  onSelectJob?: () => void
  embedded?: boolean
  deletedJobId?: string | null
}

const PAGE_SIZE = 20

export default function TestCaseHistory({ onDeleteRequest, onSelectJob, embedded, deletedJobId }: TestCaseHistoryProps) {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [filter, setFilter] = useState<'mine' | 'all' | 'starred'>('mine')
  const [historyItems, setHistoryItems] = useState<TestCaseHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE)

  const loadHistory: (retryCount?: number) => Promise<void> = useCallback(async (retryCount = 0) => {
    setIsLoading(true)
    setError(null)
    setDisplayCount(PAGE_SIZE) // Reset pagination on every fresh load
    try {
      const apiFilter = filter === 'starred' ? 'all' : filter
      const result = await getTestCaseHistory(apiFilter)
      setHistoryItems(result.jobs)
    } catch {
      if (retryCount < 1) {
        // Auto-retry once after a brief delay for transient errors
        await new Promise(resolve => setTimeout(resolve, 1000))
        return loadHistory(retryCount + 1)
      }
      setError('Could not load history. This may be a temporary issue. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [filter])

  // Auto-load history on mount and when filter changes (only after auth is ready)
  useEffect(() => {
    if (isAuthenticated) {
      loadHistory()
    }
  }, [isAuthenticated, loadHistory])

  // Remove deleted item from list without needing a refresh
  useEffect(() => {
    if (deletedJobId) {
      setHistoryItems((prev) => prev.filter((item) => item.jobId !== deletedJobId))
    }
  }, [deletedJobId])

  const handleFilterChange = (newFilter: 'mine' | 'all' | 'starred') => {
    if (newFilter !== filter) {
      setFilter(newFilter)
    }
  }

  const formatRelativeTime = (timestamp: number) => {
    const now = Date.now()
    const diff = now - timestamp
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (seconds < 60) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days === 1) return '1d ago'
    if (days < 7) return `${days}d ago`
    if (days < 30) return `${Math.floor(days / 7)}w ago`
    return new Date(timestamp).toLocaleDateString()
  }

  const handleSelectJob = (jobId: string) => {
    navigate(`/test-case/${jobId}`)
    onSelectJob?.()
  }

  const handleToggleStar = async (jobId: string, starred: boolean) => {
    // Optimistic update
    setHistoryItems((prev) => prev.map((item) => item.jobId === jobId ? { ...item, starred } : item))
    try {
      const result = await toggleStar(jobId, starred)
      if (result.deleted) {
        // Item was older than 90 days and was deleted on unstar
        setHistoryItems((prev) => prev.filter((item) => item.jobId !== jobId))
      }
    } catch (err) {
      // Revert on failure
      setHistoryItems((prev) => prev.map((item) => item.jobId === jobId ? { ...item, starred: !starred } : item))
      console.error('Failed to toggle star:', err)
    }
  }

  // Filter items based on current filter
  const filteredItems = filter === 'starred'
    ? historyItems.filter((item) => item.starred === true)
    : historyItems
  const displayedItems = filteredItems.slice(0, displayCount)

  return (
    <div className={embedded ? '' : 'border border-linear-border rounded-lg bg-linear-bg-card overflow-hidden'}>
      {/* Header bar */}
      <div className={`flex items-center justify-between px-4 py-3 border-b border-linear-border sticky top-0 z-10 ${embedded ? 'bg-linear-bg-sidebar' : 'bg-linear-bg-sidebar'}`}>
        <div className="flex items-center gap-3">
          {/* Segmented toggle */}
          <div className="flex bg-linear-bg p-0.5 rounded border border-linear-border">
            <button
              className={`px-3 py-1 text-[11px] rounded transition-colors ${
                filter === 'mine'
                  ? 'bg-linear-bg-card text-linear-text shadow-sm font-medium'
                  : 'text-linear-text-muted hover:text-linear-text-secondary'
              }`}
              onClick={() => handleFilterChange('mine')}
            >
              Mine
            </button>
            <button
              className={`px-3 py-1 text-[11px] rounded transition-colors ${
                filter === 'all'
                  ? 'bg-linear-bg-card text-linear-text shadow-sm font-medium'
                  : 'text-linear-text-muted hover:text-linear-text-secondary'
              }`}
              onClick={() => handleFilterChange('all')}
            >
              All
            </button>
            <button
              className={`px-3 py-1 text-[11px] rounded transition-colors ${
                filter === 'starred'
                  ? 'bg-linear-bg-card text-linear-text shadow-sm font-medium'
                  : 'text-linear-text-muted hover:text-linear-text-secondary'
              }`}
              onClick={() => handleFilterChange('starred')}
            >
              Starred
            </button>
          </div>
        </div>

        {/* Refresh button */}
        <button
          onClick={() => loadHistory()}
          className="text-linear-text-muted hover:text-linear-text transition-colors"
          title="Refresh"
          disabled={isLoading}
        >
          <svg className={`w-5 h-5 transition-transform ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Content area */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="spinner"></div>
          <span className="ml-2 text-linear-text-muted text-sm">Loading...</span>
        </div>
      ) : error ? (
        <div className="p-4 bg-error/10 border-b border-error/30 text-error text-sm">
          {error}
          <button
            onClick={() => loadHistory()}
            className="ml-2 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      ) : displayedItems.length === 0 ? (
        <div className="p-4 text-linear-text-muted text-sm text-center">
          {filter === 'starred'
            ? 'No starred templates yet. Star a test suite to save it as a template.'
            : filter === 'mine'
            ? "You haven't generated any test cases yet."
            : 'No test cases have been generated yet.'}
        </div>
      ) : (
        <>
        <div>
          {displayedItems.map((item) => (
            <div
              key={item.jobId}
              role="button"
              tabIndex={0}
              className="group flex items-center px-4 py-3 border-b border-linear-border hover:bg-linear-bg cursor-pointer transition-colors gap-2"
              onClick={() => handleSelectJob(item.jobId)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectJob(item.jobId) } }}
            >
              {/* Col 1: Star (fixed) */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggleStar(item.jobId, !item.starred)
                }}
                className={`flex-shrink-0 transition-colors ${
                  item.starred
                    ? 'text-linear-yellow'
                    : 'text-linear-text-muted hover:text-linear-yellow'
                }`}
                title={item.starred ? 'Remove from templates' : 'Save as template'}
                aria-label={item.starred ? 'Remove from templates' : 'Save as template'}
              >
                <svg className="w-4 h-4" fill={item.starred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>

              {/* Col 2: Title + attribution (flex, truncates) */}
              <div className="flex-1 min-w-0">
                <span className="text-sm text-linear-text truncate block group-hover:text-linear-accent transition-colors">
                  {item.testSuiteName || 'Untitled Test Suite'}
                </span>
                {filter !== 'mine' && item.userName && (
                  <span className="text-[10px] text-linear-text-muted truncate block">by {item.userName}</span>
                )}
              </div>

              {/* Col 3: Coverage stats (fixed width, right-aligned) - hidden on narrow screens */}
              <span className="history-coverage-stats flex items-center gap-2 text-[11px] text-linear-text-muted flex-shrink-0 w-[72px]">
                {item.typeBreakdown.happyPath > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                    {item.typeBreakdown.happyPath}
                  </span>
                )}
                {item.typeBreakdown.sadPath > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-error"></span>
                    {item.typeBreakdown.sadPath}
                  </span>
                )}
                {item.typeBreakdown.edgeCase > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-warning"></span>
                    {item.typeBreakdown.edgeCase}
                  </span>
                )}
              </span>

              {/* Col 4: Date (fixed width, right-aligned) */}
              <span className="text-[11px] text-linear-text-muted flex-shrink-0 w-[70px] text-right whitespace-nowrap">
                {formatRelativeTime(item.createdAt)}
              </span>

              {/* Col 5: Actions spacer (fixed width, always present for alignment) */}
              <div className="flex items-center flex-shrink-0 w-[28px] justify-end">
                {item.isOwner && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteRequest(item.jobId)
                    }}
                    className="p-1 hover:bg-linear-bg-sidebar rounded text-linear-text-muted hover:text-error transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete"
                    aria-label="Delete test case"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* Load more / pagination footer */}
        {filteredItems.length > displayCount && (
          <>
            <div className="px-4 pt-2 pb-1 flex justify-end">
              <span className="text-[10px] text-linear-text-muted">
                Showing {displayCount} of {filteredItems.length}
              </span>
            </div>
            <button
              onClick={() => setDisplayCount((c) => Math.min(c + PAGE_SIZE, filteredItems.length))}
              className="w-full py-2.5 text-[12px] text-linear-text-muted hover:text-linear-accent hover:bg-linear-bg transition-colors border-t border-linear-border"
            >
              Load {Math.min(PAGE_SIZE, filteredItems.length - displayCount)} more
            </button>
          </>
        )}
        </>
      )}
    </div>
  )
}
